import { computed, makeObservable, observable } from 'mobx'

import { SyncStore } from './syncStore'
import { saveMpCardNavContext } from '../mpCard/shared/lib/mpCardNavContext'
import { AdminStore } from './adminStore'
import { LabelStore } from './labelStore'
import { OrderStore } from './orderStore'
import { ProductStore } from './productStore'
import { PasswordResetStore } from './passwordResetStore'

import { required } from '../common/objectUtils'
import { UnprocessedValueError, valuesOf } from '../common/tsUtils'

import { DialogInfo } from '../types/dialogInfo'
import { myWbColumns } from '../types/wbColumns'
import { myOzonColumns } from '../types/ozonColumns'
import { NavStore } from '../types/navStore'
import { MyProductColumn } from '../types/myProductColumnTypes'
import { createMyStorageColumns } from '../types/myStorageColumns'
import { myYandexMarketColumns } from '../types/yandexMarketColumns'
import { AllowedIntegrationsMap, StoreIntegrationType } from '../types/integrationTypeUtils'
import { MyProductColumnGroupId } from '../types/myProductColumnGroups'
import { MarketplaceProductColumnId, MyProductColumnId, syncErrorsColumnId } from '../types/myProductColumnIds'

import { NavState } from '../server/mpsklad_core/Models/NavState'
import { MoySkladAppType } from '../server/mpsklad_common/MoySkladAppType'
import { UserAuthModel } from '../server/mpsklad_core/Models/UserAuthModel'
import { MyProductState } from '../server/mpsklad_core/Models/MyProductState'
import { WbAccountModel } from '../server/mpsklad_core/Models/WbAccountModel'
import { IntegrationType } from '../server/mpsklad_core/Entity/IntegrationType'
import { OzonAccountModel } from '../server/mpsklad_core/Models/OzonAccountModel'
import { YandexMarketAccountModel } from '../server/mpsklad_core/Models/YandexMarketAccountModel'
import { MoySkladAccountModel } from '../server/mpsklad_core/Models/MoySkladAccountModel'
import { MpCardStore } from './mpcardStore'

export class Store {
  @observable
  user: UserAuthModel | null

  /**
   * Alternative to cookie auth.
   * Used for MS app, because Safari doesn't allow third party iframe cookies.
   */
  @observable
  authToken: string | null

  /**
   * NOTE: Don't use for reactions - not readonly.
   * NOTE: Can't make the type writable - causes inconsistent initial state.
   * Use {@link getHomeNav} instead.
   */
  @observable
  private _homeNav: NavStore | null

  @observable
  readonly syncStore: SyncStore

  @observable
  readonly productStore: ProductStore

  @observable
  readonly orderStore: OrderStore

  @observable
  readonly labelStore: LabelStore

  @observable
  readonly adminStore: AdminStore

  @observable
  readonly passwordResetStore: PasswordResetStore

  @observable
  readonly mpcardStore: MpCardStore

  @observable.ref
  dialog: DialogInfo | null

  /**
   * Ids of hidden column are stored instead of visible to automatically show future columns.
   * TODO: Move inside TableData?
   */
  @observable
  hiddenMyProductColumnIds: Set<MyProductColumnId | MarketplaceProductColumnId>

  constructor() {
    makeObservable(this)

    this.user = null
    this.authToken = null

    this._homeNav = {
      accountId: undefined,
      integrationType: IntegrationType.MoySklad,
      productsStateTab: MyProductState.Active
    }

    this.syncStore = new SyncStore()
    this.productStore = new ProductStore()
    this.orderStore = new OrderStore()
    this.labelStore = new LabelStore()

    this.adminStore = new AdminStore()
    this.passwordResetStore = new PasswordResetStore()
    this.mpcardStore = new MpCardStore()

    // TODO: UI store?
    this.dialog = null
    this.hiddenMyProductColumnIds = new Set()
  }

  @computed
  get hasAuth(): boolean {
    return this.user !== null
  }

  @computed
  get hasMoySkladApp(): boolean {
    return this.user?.moySkladAppState != null
  }

  canAddStoreAccount =
    (integrationType: IntegrationType): boolean => {
      switch (integrationType) {
        case IntegrationType.MoySklad:
          return false

        case IntegrationType.Ozon:
          return this.canAddOzonAccount

        case IntegrationType.Wildberries:
          return this.canAddWbAccount

        case IntegrationType.YandexMarket:
          return this.canAddYandexMarketAccount

        default:
          throw new UnprocessedValueError('IntegrationType', integrationType)
      }
    }

  @computed
  get canAddYandexMarketAccount(): boolean {
    if (this.user == null) {
      return false
    }

    const {moySkladAppType} = this.user

    return moySkladAppType == null
           || moySkladAppType === MoySkladAppType.Ym
           || moySkladAppType === MoySkladAppType.UzbYm
           || moySkladAppType === MoySkladAppType.OzonAndWb
  }

  @computed
  get canAddOzonAccount(): boolean {
    if (this.user == null) {
      return false
    }

    const {moySkladAppType} = this.user

    return moySkladAppType == null
           || moySkladAppType === MoySkladAppType.Ozon
           || moySkladAppType === MoySkladAppType.OzonAndWb
           || (moySkladAppType === MoySkladAppType.Mini && this.syncStore.ozonAccounts.length < 2)
  }

  @computed
  get canAddWbAccount(): boolean {
    if (this.user == null) {
      return false
    }

    const {moySkladAppType} = this.user

    return moySkladAppType == null
           || moySkladAppType === MoySkladAppType.Wb
           || moySkladAppType === MoySkladAppType.OzonAndWb
           || (moySkladAppType === MoySkladAppType.Mini && this.syncStore.wbAccounts.length < 2)
  }

  getHomeNav =
    (): NavStore | null => {
      // NOTE: Making this property a class method breaks some mobx reactions.
      // NOTE: This is a method instead of a getter to prevent tracking nested properties directly.
      // NOTE: _homeNav must not be tracked directly - it's not readonly.
      return this._homeNav
    }

  /**
   * NOTE: Don't track via mobx.
   * Using within observer components should be fine because of destructuring.
   */
  @computed
  get homeNavRequired(): NavStore {
    if (this._homeNav == null) {
      throw new Error('homeNav is not initialized')
    }

    return this._homeNav
  }

  /**
   * NOTE: Don't track via mobx.
   * Using within observer components should be fine because of destructuring.
   */
  @computed
  get navState(): NavState {
    const homeNav = this.homeNavRequired

    return {
      integrationType: homeNav.integrationType,
      accountId: required(homeNav.accountId)
    }
  }

  getHomeOzonAccount =
    (): OzonAccountModel => {
      if (this.homeNavRequired.integrationType !== IntegrationType.Ozon) {
        throw new Error(
          `Bad home nav account type: ${this.homeNavRequired.integrationType}, expected ${IntegrationType.Ozon}`)
      }

      const homeNavAccountId = required(this.homeNavRequired.accountId)
      return required(this.syncStore.ozonAccounts.find(_ => _.id === homeNavAccountId))
    }

  getHomeWbAccount =
    (): WbAccountModel => {
      if (this.homeNavRequired.integrationType !== IntegrationType.Wildberries) {
        throw new Error(
          `Bad home nav account type: ${this.homeNavRequired.integrationType}, expected ${IntegrationType.Wildberries}`)
      }

      const homeNavAccountId = required(this.homeNavRequired.accountId)
      return required(this.syncStore.wbAccounts.find(_ => _.id === homeNavAccountId))
    }

  getHomeYmAccount =
    (): YandexMarketAccountModel => {
      if (this.homeNavRequired.integrationType !== IntegrationType.YandexMarket) {
        throw new Error(
          `Bad home nav account type: ${this.homeNavRequired.integrationType}, expected ${IntegrationType.YandexMarket}`)
      }

      const homeNavAccountId = required(this.homeNavRequired.accountId)
      return required(this.syncStore.yandexMarketAccounts.find(_ => _.id === homeNavAccountId))
    }

  getHomeMoySkladAccount =
    (): MoySkladAccountModel => {
      if (this.homeNavRequired.integrationType !== IntegrationType.MoySklad) {
        throw new Error(
          `Bad home nav account type: ${this.homeNavRequired.integrationType}, expected ${IntegrationType.MoySklad}`)
      }

      return required(this.syncStore.moySkladAccount)
    }

  getHomeAccount =
    (): OzonAccountModel | WbAccountModel | YandexMarketAccountModel => {
      switch (this.homeNavRequired.integrationType) {
        case IntegrationType.Ozon:
          return this.getHomeOzonAccount()

        case IntegrationType.Wildberries:
          return this.getHomeWbAccount()

        case IntegrationType.YandexMarket:
          return this.getHomeYmAccount()

        default:
          throw new Error(`Unprocessed store type: ${this.homeNavRequired.integrationType}`)
      }
    }

  getHomeAccountWithMS =
    (): OzonAccountModel | WbAccountModel | YandexMarketAccountModel | MoySkladAccountModel => {
      switch (this.homeNavRequired.integrationType) {
        case IntegrationType.Ozon:
          return this.getHomeOzonAccount()

        case IntegrationType.Wildberries:
          return this.getHomeWbAccount()

        case IntegrationType.YandexMarket:
          return this.getHomeYmAccount()

        case IntegrationType.MoySklad:
          return this.getHomeMoySkladAccount()

        default:
          throw new Error(`Unprocessed store type: ${this.homeNavRequired.integrationType}`)
      }
    }

  homeHasStoreType =
    (integrationType: StoreIntegrationType): boolean =>
      this.homeNavRequired.integrationType === integrationType
      && this.syncStore.getStoreAccounts(integrationType).length > 0

  /**
   * TODO: (redesign) Check usages
   */
  setHomeNav =
    (integrationType: IntegrationType,
     accountId: number | undefined) => {
      if (accountId && accountId <= 0) {
        throw new Error('accountId must be positive')
      }

      this._homeNav = {
        integrationType,
        accountId,
        productsStateTab: MyProductState.Active
      }

      if (accountId != null) {
        saveMpCardNavContext({integrationType, accountId})
      }
    }

  setNavFirstAccount =
    (integrationType: IntegrationType,
     accountId: number | undefined) => {
      if (accountId && accountId <= 0) {
        throw new Error('accountId must be positive')
      }

      const nav = this.getHomeNav()

      if (!nav) {
        return this._homeNav = {
          integrationType,
          accountId,
          productsStateTab: MyProductState.Active
        }
      }

      this._homeNav = {
        ...nav,
        integrationType,
        accountId
      }
    }

  unsetHomeNavAccount = () => {
    if (!this._homeNav) {
      return
    }

    this._homeNav = {...this._homeNav, accountId: undefined}
  }

  setHomeTab =
    (value: MyProductState) => {
      this._homeNav = {
        ...this.homeNavRequired,
        productsStateTab: value
      }
    }

  @computed
  get myProductColumnsGrouped(): {
    readonly [Group in MyProductColumnGroupId]: MyProductColumn[]
  } {
    return {
      [MyProductColumnGroupId.MyStorage]:
        createMyStorageColumns(
          storeType => this.homeHasStoreType(storeType),
          this.allowedIntegrations),

      [MyProductColumnGroupId.Ozon]:
        this.homeHasStoreType(IntegrationType.Ozon) && this.allowedIntegrations[IntegrationType.Ozon]
        ? myOzonColumns
        : [],

      [MyProductColumnGroupId.Wildberries]:
        this.homeHasStoreType(IntegrationType.Wildberries) && this.allowedIntegrations[IntegrationType.Wildberries]
        ? myWbColumns
        : [],

      [MyProductColumnGroupId.YandexMarket]:
        this.homeHasStoreType(IntegrationType.YandexMarket) && this.allowedIntegrations[IntegrationType.YandexMarket]
        ? myYandexMarketColumns
        : []
    }
  }

  @computed
  get myProductColumns() {
    return valuesOf(this.myProductColumnsGrouped).flatMap((_) => _)
  }

  // TODO: Move inside TableData?
  @computed
  get visibleMyProductColumns(): MyProductColumn[] {
    let visibleColumns = this.myProductColumns.filter(column => !this.hiddenMyProductColumnIds.has(column.id))

    if (this.homeNavRequired.productsStateTab !== MyProductState.Error) {
      visibleColumns = visibleColumns.filter(column => column.id !== syncErrorsColumnId)
    }

    return visibleColumns
  }

  @computed.struct
  get allowedIntegrations(): AllowedIntegrationsMap /* type is required */ {
    // NOTE: Making this a method will likely prevent useMemo from working properly
    const msAppType = this.user?.moySkladAppType

    switch (msAppType) {
      case null:
      case undefined:
        return {
          [IntegrationType.Ozon]: true,
          [IntegrationType.Wildberries]: true,
          [IntegrationType.YandexMarket]: true,
          [IntegrationType.MoySklad]: true
        }

      // Account checking is only for existing accounts of Ozon and Wb - old MS app limitations were bad
      case MoySkladAppType.Ozon:
        return {
          [IntegrationType.Ozon]: true,
          [IntegrationType.Wildberries]: this.syncStore.wbAccounts.length > 0,
          [IntegrationType.YandexMarket]: false,
          [IntegrationType.MoySklad]: true
        }

      case MoySkladAppType.Wb:
        return {
          [IntegrationType.Ozon]: this.syncStore.ozonAccounts.length > 0,
          [IntegrationType.Wildberries]: true,
          [IntegrationType.YandexMarket]: false,
          [IntegrationType.MoySklad]: true
        }

      case MoySkladAppType.OzonAndWb:
        return {
          [IntegrationType.Ozon]: true,
          [IntegrationType.Wildberries]: true,
          [IntegrationType.YandexMarket]: true,
          [IntegrationType.MoySklad]: true
        }

      case MoySkladAppType.Mini:
        return {
          [IntegrationType.Ozon]: true,
          [IntegrationType.Wildberries]: true,
          [IntegrationType.YandexMarket]: false,
          [IntegrationType.MoySklad]: true
        }

      case MoySkladAppType.Ym:
      case MoySkladAppType.UzbYm:
        return {
          [IntegrationType.Ozon]: false,
          [IntegrationType.Wildberries]: false,
          [IntegrationType.YandexMarket]: true,
          [IntegrationType.MoySklad]: true
        }

      default:
        throw new UnprocessedValueError('MoySkladAppType', msAppType)
    }
  }
}